// =============================================================================
// MeuDriver — M3: Hook de Certificados de Conclusão
// =============================================================================

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/hooks/useAuth'
import { toast } from 'sonner'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface CertificateRow {
  id: string
  student_id: string
  instructor_id: string
  certificate_number: string
  total_lessons: number
  total_hours: number
  issued_at: string
  student?: { full_name: string | null; avatar_url: string | null } | null
  instructor?: {
    id: string
    profile?: { full_name: string | null } | null
  } | null
}

export interface EligibleStudent {
  student_id: string
  full_name: string | null
  avatar_url: string | null
  completed_lessons: number
  total_hours: number
  already_certified: boolean
}

// ---------------------------------------------------------------------------
// useMyCertificates — aluno lista os próprios certificados
// ---------------------------------------------------------------------------

export function useMyCertificates() {
  const { user } = useAuth()

  return useQuery<CertificateRow[]>({
    queryKey: ['my-certificates', user?.id],
    enabled: !!user?.id,
    staleTime: 1000 * 60 * 5,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('student_certificates')
        .select(`
          id, student_id, instructor_id, certificate_number,
          total_lessons, total_hours, issued_at,
          instructor:instructors!student_certificates_instructor_id_fkey(
            id,
            profile:profiles!instructors_profile_id_fkey(full_name)
          )
        `)
        .eq('student_id', user!.id)
        .order('issued_at', { ascending: false })

      if (error) throw error
      return (data ?? []) as CertificateRow[]
    },
  })
}

// ---------------------------------------------------------------------------
// useIssuedCertificates — instrutor lista certificados que emitiu
// ---------------------------------------------------------------------------

export function useIssuedCertificates(instructorId: string | null) {
  return useQuery<CertificateRow[]>({
    queryKey: ['issued-certificates', instructorId],
    enabled: !!instructorId,
    staleTime: 1000 * 60 * 5,
    queryFn: async () => {
      if (!instructorId) return []
      const { data, error } = await supabase
        .from('student_certificates')
        .select(`
          id, student_id, instructor_id, certificate_number,
          total_lessons, total_hours, issued_at,
          student:profiles!student_certificates_student_id_fkey(full_name, avatar_url)
        `)
        .eq('instructor_id', instructorId)
        .order('issued_at', { ascending: false })

      if (error) throw error
      return (data ?? []) as CertificateRow[]
    },
  })
}

// ---------------------------------------------------------------------------
// useEligibleStudents — alunos com >= 10 aulas concluídas com o instrutor
// ---------------------------------------------------------------------------

export function useEligibleStudents(instructorId: string | null) {
  return useQuery<EligibleStudent[]>({
    queryKey: ['eligible-students', instructorId],
    enabled: !!instructorId,
    staleTime: 1000 * 60 * 5,
    queryFn: async () => {
      if (!instructorId) return []

      // Busca bookings concluídos agrupando por aluno
      const { data: bookings } = await supabase
        .from('bookings')
        .select(`
          student_id, duration_minutes,
          student:profiles!bookings_student_id_fkey(full_name, avatar_url)
        `)
        .eq('instructor_id', instructorId)
        .eq('status', 'completed')

      if (!bookings) return []

      // Busca certificados já emitidos para não duplicar
      const { data: certs } = await supabase
        .from('student_certificates')
        .select('student_id')
        .eq('instructor_id', instructorId)

      const certifiedIds = new Set((certs ?? []).map((c) => c.student_id))

      // Agrupa por aluno
      const map = new Map<
        string,
        { full_name: string | null; avatar_url: string | null; lessons: number; minutes: number }
      >()

      for (const b of bookings) {
        const profile = b.student as { full_name: string | null; avatar_url: string | null } | null
        const existing = map.get(b.student_id)
        if (existing) {
          existing.lessons += 1
          existing.minutes += b.duration_minutes ?? 60
        } else {
          map.set(b.student_id, {
            full_name: profile?.full_name ?? null,
            avatar_url: profile?.avatar_url ?? null,
            lessons: 1,
            minutes: b.duration_minutes ?? 60,
          })
        }
      }

      return Array.from(map.entries())
        .filter(([, v]) => v.lessons >= 10)
        .map(([student_id, v]) => ({
          student_id,
          full_name: v.full_name,
          avatar_url: v.avatar_url,
          completed_lessons: v.lessons,
          total_hours: parseFloat((v.minutes / 60).toFixed(1)),
          already_certified: certifiedIds.has(student_id),
        }))
        .sort((a, b) => b.completed_lessons - a.completed_lessons)
    },
  })
}

// ---------------------------------------------------------------------------
// useIssueCertificate — instrutor emite certificado
// ---------------------------------------------------------------------------

export function useIssueCertificate() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      studentId,
      instructorId,
      totalHours,
    }: {
      studentId: string
      instructorId: string
      totalHours: number
    }) => {
      const { data, error } = await supabase.rpc('issue_student_certificate', {
        p_student_id: studentId,
        p_instructor_id: instructorId,
        p_total_hours: totalHours,
      })
      if (error) throw error
      return data as string // UUID do certificado
    },
    onSuccess: (_, { instructorId }) => {
      toast.success('Certificado emitido com sucesso!')
      queryClient.invalidateQueries({ queryKey: ['issued-certificates', instructorId] })
      queryClient.invalidateQueries({ queryKey: ['eligible-students', instructorId] })
    },
    onError: (err: Error) => {
      const msg = err.message.includes('Mínimo: 10')
        ? err.message
        : `Erro ao emitir certificado: ${err.message}`
      toast.error(msg)
    },
  })
}

// ---------------------------------------------------------------------------
// VerifyCertificateResult — resposta da RPC verify_certificate
// ---------------------------------------------------------------------------

export interface VerifyCertificateResult {
  valid: boolean
  certificate_number?: string
  student_name?: string
  instructor_name?: string
  total_lessons?: number
  total_hours?: number
  issued_at?: string
}

// ---------------------------------------------------------------------------
// useVerifyCertificate — verificação pública por número via RPC (CRIT-001)
// Usa supabase.rpc('verify_certificate') em vez de query direta na tabela,
// evitando que a policy USING(true) exponha todos os certificados.
// ---------------------------------------------------------------------------

export function useVerifyCertificate(certificateNumber: string | null) {
  return useQuery<VerifyCertificateResult | null>({
    queryKey: ['verify-certificate', certificateNumber],
    enabled: !!certificateNumber && certificateNumber.trim().length > 0,
    staleTime: 1000 * 60 * 60, // 1h — número de certificado não muda
    queryFn: async () => {
      if (!certificateNumber) return null

      const { data, error } = await supabase.rpc('verify_certificate', {
        p_certificate_number: certificateNumber.trim().toUpperCase(),
      })

      if (error) throw error
      return (data ?? null) as VerifyCertificateResult | null
    },
  })
}
